import { useRef, useState } from "react";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Check, X, Eraser, Ban, RefreshCw } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

/**
 * Reusable signature pad wrapper. Renders a Radix-styled canvas + name input
 * OR shows the persisted signature/refusal in a read-only state.
 *
 * Props:
 *   label
 *   existing              current signature object (or null)
 *   onSave({file_id,name}) called after uploading PNG + client-side clear
 *   allowRefused          when true, show a "Customer refused" toggle
 *   onRefuse(reason)      called when refused mode is submitted
 *   testIdPrefix
 */
export default function SignaturePadField({ label, existing, onSave, allowRefused = false, onRefuse, testIdPrefix }) {
  const [name, setName] = useState(existing && !existing.refused ? existing.name || "" : "");
  const [refuseMode, setRefuseMode] = useState(Boolean(existing?.refused));
  const [refuseReason, setRefuseReason] = useState(existing?.refused ? existing.reason || "" : "");
  const [saving, setSaving] = useState(false);
  const [replace, setReplace] = useState(false);
  const canvasRef = useRef(null);

  const hasPersisted = Boolean(existing) && !replace;

  const clear = () => canvasRef.current?.clear();
  const isEmpty = () => canvasRef.current?.isEmpty();

  const save = async () => {
    if (isEmpty()) return toast.error("Please sign first");
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.getTrimmedCanvas().toDataURL("image/png");
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `signature-${Date.now()}.png`, { type: "image/png" });
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "signature");
      const { data: f } = await api.post("/files", form, { headers: { "Content-Type": "multipart/form-data" } });
      await onSave({ file_id: f.id, name: name.trim() });
      clear();
      setReplace(false);
      toast.success(`${label} saved`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const submitRefused = async () => {
    if (!refuseReason.trim()) return toast.error("Reason is required");
    setSaving(true);
    try {
      await onRefuse(refuseReason.trim());
      setReplace(false);
      toast.success("Refusal recorded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (hasPersisted) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-persisted`}>
        <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
        {existing.refused ? (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 space-y-1" data-testid={`${testIdPrefix}-refused`}>
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center gap-1"><Ban className="h-3.5 w-3.5" /> Refused</div>
            <div className="text-sm">{existing.reason}</div>
          </div>
        ) : (
          <div className="border border-border rounded-sm p-3 bg-background" data-testid={`${testIdPrefix}-signed`}>
            {existing.file_id && (
              <img
                src={`/api/files/${existing.file_id}`}
                alt="signature"
                className="max-h-24 bg-white rounded-sm border border-border"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            )}
            <div className="text-sm mt-2 flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-600" /><span className="font-medium">{existing.name}</span></div>
            {existing.signed_at && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Signed {new Date(existing.signed_at).toLocaleString()}</div>}
          </div>
        )}
        <Button size="sm" variant="ghost" className="text-xs gap-1" onClick={() => { setReplace(true); setRefuseMode(false); }} data-testid={`${testIdPrefix}-replace`}>
          <RefreshCw className="h-3 w-3" /> Replace
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
        {allowRefused && (
          <button
            type="button"
            onClick={() => setRefuseMode((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            data-testid={`${testIdPrefix}-refuse-toggle`}
          >
            <Ban className="h-3.5 w-3.5" /> {refuseMode ? "Draw signature instead" : "Customer refused / unavailable"}
          </button>
        )}
      </div>

      {refuseMode ? (
        <div className="space-y-2">
          <Textarea
            value={refuseReason}
            onChange={(e) => setRefuseReason(e.target.value)}
            rows={3}
            placeholder="Reason (required)"
            className="text-base"
            data-testid={`${testIdPrefix}-refuse-reason`}
          />
          <Button className="min-h-11 w-full" onClick={submitRefused} disabled={saving} data-testid={`${testIdPrefix}-refuse-submit`}>
            {saving ? "Saving…" : "Record refusal"}
          </Button>
        </div>
      ) : (
        <>
          <div className="border border-border rounded-sm bg-white" data-testid={`${testIdPrefix}-canvas-wrap`}>
            <SignatureCanvas
              ref={canvasRef}
              penColor="black"
              canvasProps={{
                className: "w-full h-40",
                "data-testid": `${testIdPrefix}-canvas`,
              }}
            />
          </div>
          <Input
            placeholder="Print name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="text-base min-h-11"
            data-testid={`${testIdPrefix}-name`}
          />
          <div className="flex gap-2">
            <Button variant="outline" className="min-h-11 flex-1 gap-1" onClick={clear} data-testid={`${testIdPrefix}-clear`}>
              <Eraser className="h-4 w-4" /> Clear
            </Button>
            {existing && (
              <Button variant="ghost" className="min-h-11" onClick={() => setReplace(false)} data-testid={`${testIdPrefix}-cancel`}>
                <X className="h-4 w-4" />
              </Button>
            )}
            <Button className="min-h-11 flex-1 gap-1" onClick={save} disabled={saving} data-testid={`${testIdPrefix}-save`}>
              <Check className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
